import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Table, Tooltip, Avatar, Drawer } from 'antd';
import classNames from 'classnames';
import uniqBy from 'lodash/uniqBy';

import { pageChanged } from '../../../../../data/redux/page_details/actions';
import { getAllIntents } from '../../../../../data/redux/nlp_details/actions';
import * as chatlogActions from '../../../../../data/redux/chatlog_details/actions';

import { APP_PAGES, ORIENTATION } from '../../../../../data/config/constants';
import { uniqueId } from '../../../../../data/config/utils';

import CardContainer from '../../../components/cardcontainer';
import CustomHeader from '../../../../../components/customheader';
import CustomPagination from '../../../../../components/custompagination';
import ErrorBoundary from '../../../../../components/errorboundary';
import Graph from '../../../components/charts/graph';
import MessageTrainingForm from './components/messagetrainingform';

const message_statistics_granularity_options = [
    { value: 'hour', name: 'Hour' },
    { value: 'day', name: 'Day' },
    { value: 'week', name: 'Week' },
    { value: 'month', name: 'Month' },
];

const untrain_granularity = [
    { value: true, name: 'Handled' },
    { value: false, name: 'Unhandled' },
];

class ChatlogAnalytics extends Component {
    constructor(props) {
        super(props);
        this.trainingMessageTableColumn = this.getTrainingMessageTableColumn();
        this.state = {
            drawer_data: {
                title: "",
                show_message_training_form: false,
                active_message: null,
                suggestions: []
            },
        };
    }

    componentWillMount() {
        const { actions } = this.props;
        actions.pageChanged(APP_PAGES.CHATLOG_ANALYTICS, APP_PAGES.ANALYTICS);
    }

    componentDidMount() {
        const { actions, chatlog_details } = this.props;
        actions.getAllIntents();
        if (chatlog_details.message_time_series.length === 0) {
            actions.getMessageTimeSeries(chatlog_details.message_filters);
        }
        if (chatlog_details.training_messages.length === 0) {
            actions.getTrainingMessages(chatlog_details.training_message_filters);
        }
    }

    getTrainingMessageTableColumn = () => {
        const column = [
            {
                title: 'Type',
                dataIndex: 'isTrained',
                key: 'isTrained',
                render: (text, record) => (<Tooltip placement="right" overlayClassName="ori-font-xs ori-tooltip-arrow-hidden ori-tooltip-bg-white ori-tooltip-tag" title={<span>{record.updatedNLPAnalysisSnapshot ? "Trained" : "Untrained"}</span>}><Avatar size="small" className={classNames({ "ori-bg-green": record.updatedNLPAnalysisSnapshot, "ori-bg-danger": !record.updatedNLPAnalysisSnapshot })}>{record.updatedNLPAnalysisSnapshot ? "T" : "U"}</Avatar></Tooltip>)
            },
            {
                title: 'User Message',
                dataIndex: 'text',
                key: 'text',
                render: (text, record) => (<span className="ori-link-primary" onClick={() => this.fetchMessageSuggestions(record)}>{text}</span>)
            },
            {
                title: 'Intent',
                dataIndex: 'intent',
                key: 'intent',
                render: (text, record) => {
                    if (record.updatedNLPAnalysisSnapshot && record.updatedNLPAnalysisSnapshot.intentSnapshot && record.updatedNLPAnalysisSnapshot.intentSnapshot.intent) {
                        return (
                            <div className="ori-flex-row">
                                <div className="ori-lr-pad-6 ori-bg-default ori-border-radius-10 ori-font-11 ori-line-through">{record.intent}</div>
                                <Tooltip overlayClassName="intentTooltip" placement="bottomLeft" title={record.updatedNLPAnalysisSnapshot.intentSnapshot.score}>
                                    <div className="ori-lr-pad-6 ori-bg-default ori-l-mrgn-5 ori-border-radius-10 ori-font-11 ori-font-primary">{record.updatedNLPAnalysisSnapshot.intentSnapshot.intent}</div>
                                </Tooltip>
                            </div>
                        );
                    }
                    else {
                        return (
                            <div className="ori-flex-row"><div className="ori-lr-pad-6 ori-bg-default ori-border-radius-10 ori-font-11 ori-font-primary">{record.intent}</div></div>
                        );
                    }
                }
            },
            {
                title: 'Count',
                dataIndex: 'count',
                key: 'count',
                align: 'center',
                render: count => (<span className="ori-font-lg ori-font-primary">{count}</span>)
            },
            {
                title: 'Rate of Exit',
                dataIndex: 'exitRate',
                key: 'exitRate',
                align: 'center',
                render: exitRate => (<span className="ori-font-danger"><span className="ori-font-lg">{exitRate}</span> %</span>)
            },
        ];
        return column;
    };

    updateMessageTimeSeriesTickInterval = (response, granularity) => {
        const { actions, chatlog_details } = this.props;
        let offset = 1;
        const hour = 60 * 60 * 1000;
        if (response && response.messagesTimeSeries) {
            const uniqTypes = uniqBy(response.messagesTimeSeries, 'type');
            offset = parseInt(response.messagesTimeSeries.length / (uniqTypes.length * 10));
            offset = offset >= 1 ? offset : 1;
        }
        const timestamp = {
            ...chatlog_details.message_scale.timestamp,
            tickInterval: granularity === "day" ? hour * 24 * offset : (granularity === "week" ? hour * 24 * 7 * offset : (granularity === "month" ? hour * 24 * 30 * offset : hour * offset)),
            mask: granularity === "hour" ? 'DD MMM HH:mm' : 'DD MMM',
        };
        actions.updateChatlogState(timestamp, 'message_scale.timestamp');
    };

    handleMessageDateRangeChange = date_range => {
        const { actions, chatlog_details } = this.props;
        const num_of_days = date_range[1].diff(date_range[0], 'days') + 1;
        let granularity = chatlog_details.message_filters.granularity;
        if (num_of_days === 1) {
            granularity = 'hour';
        } else if (num_of_days <= 7) {
            granularity = 'day';
        } else if (num_of_days <= 60) {
            granularity = 'week';
        } else {
            granularity = 'month';
        }
        const payload = {
            ...chatlog_details.message_filters,
            startDate: date_range[0],
            endDate: date_range[1],
            granularity,
        };
        actions.getMessageTimeSeries(payload, (res) => {
            actions.updateChatlogState(date_range[0], "message_filters.startDate");
            actions.updateChatlogState(date_range[1], "message_filters.endDate");
            actions.updateChatlogState(granularity, "message_filters.granularity");
            this.updateMessageTimeSeriesTickInterval(res, granularity);
        });
    };

    handleMessagePlatformChange = platform => {
        const { actions, chatlog_details } = this.props;
        const payload = { ...chatlog_details.message_filters, platform };
        actions.getMessageTimeSeries(payload, () => actions.updateChatlogState(platform, "message_filters.platform"));
    };

    handleMessageGranularityChange = event => {
        const granularity = event.target.value;
        const { actions, chatlog_details } = this.props;
        const payload = { ...chatlog_details.message_filters, granularity };
        actions.getMessageTimeSeries(payload, (res) => {
            actions.updateChatlogState(granularity, "message_filters.granularity");
            this.updateMessageTimeSeriesTickInterval(res, granularity);
        });
    };

    handleTrainingMessagesDateRangeChange = date_range => {
        const { actions, chatlog_details } = this.props;
        const payload = {
            ...chatlog_details.training_message_filters,
            startDate: date_range[0],
            endDate: date_range[1],
        };
        actions.getTrainingMessages(payload, () => {
            actions.updateChatlogState(date_range[0], "training_message_filters.startDate");
            actions.updateChatlogState(date_range[1], "training_message_filters.endDate");
        });
    };

    handleTrainingMessageGranularityChange = event => {
        const isHandled = event.target.value;
        const { actions, chatlog_details } = this.props;
        const payload = { ...chatlog_details.training_message_filters, isHandled };
        actions.getTrainingMessages(payload, () => actions.updateChatlogState(isHandled, "training_message_filters.isHandled"));
    };

    handleTrainingMessageSwitchChange = isTrained => {
        const { actions, chatlog_details } = this.props;
        const payload = { ...chatlog_details.training_message_filters, isTrained, isHandled: null };
        actions.getTrainingMessages(payload, () => actions.updateChatlogState(isTrained, "training_message_filters.isTrained"));
    };

    handleTrainingMessageSearch = searchKey => {
        const { actions, chatlog_details } = this.props;
        const payload = { ...chatlog_details.training_message_filters, searchKey };
        actions.getTrainingMessages(payload, () => actions.updateChatlogState(searchKey, "training_message_filters.searchKey"));
    };

    fetchTrainingMessage = () => {
        const { actions, chatlog_details } = this.props;
        actions.getTrainingMessages(chatlog_details.training_message_filters);
    };

    onChangeTrainingMessagePagination = current => {
        if (current) {
            const { actions, chatlog_details } = this.props;
            const payload = {
                ...chatlog_details.training_message_filters,
                skip: (current - 1) * chatlog_details.training_message_filters.limit,
            };
            actions.getTrainingMessages(payload, () => actions.updateChatlogState(current, "training_message_pagination.current"));
        }
    };

    fetchMessageSuggestions = active_message => {
        const { actions } = this.props;
        actions.getTrainingMessageSuggestions({ text: active_message.text }, (suggestions) => {
            const currentIntent = active_message.updatedNLPAnalysisSnapshot && active_message.updatedNLPAnalysisSnapshot.intentSnapshot ? active_message.updatedNLPAnalysisSnapshot.intentSnapshot.intent : active_message.intent;
            this.setState({
                drawer_data: {
                    title: active_message.text,
                    show_message_training_form: true,
                    active_message: {
                        text: active_message.text,
                        oldIntent: currentIntent,
                        newIntent: currentIntent,
                        utterances: [],
                        isTrained: !!active_message.updatedNLPAnalysisSnapshot,
                    },
                    suggestions
                }
            });
        });
    };

    updateActiveMessage = (key, value) => {
        this.setState({
            drawer_data: {
                ...this.state.drawer_data,
                active_message: {
                    ...this.state.drawer_data.active_message,
                    [key]: value,
                }
            }
        });
    };

    onChangeIntent = intent => this.updateActiveMessage('newIntent', intent === undefined ? null : intent);

    onChangeUtterances = utterances => this.updateActiveMessage('utterances', utterances);

    closeModal = () => {
        this.setState({
            drawer_data: {
                title: "",
                show_message_training_form: false,
                active_message: null,
                suggestions: [],
            }
        });
    };

    trainMessage = () => {
        const { actions } = this.props;
        const { active_message } = this.state.drawer_data;
        actions.trainMessage(active_message, this.closeModal);
    };

    renderMessageTimeSeries = () => {
        const { allowedChannels } = this.props;
        const { message_filters, message_time_series, message_scale, loaders } = this.props.chatlog_details;
        const custom_header_props = {
            title: "Message Statistics",
            date_range: [message_filters.startDate, message_filters.endDate],
            onChangeDateRange: this.handleMessageDateRangeChange,
            dropdown_value: message_filters.platform,
            dropdown_options: [{ value: 'all', name: 'All' }, ...allowedChannels],
            onChangeDropdown: this.handleMessagePlatformChange,
            granularity_value: message_filters.granularity,
            granularity_options: message_statistics_granularity_options,
            onChangeGranularity: this.handleMessageGranularityChange,
            show_date_range: true,
            show_dropdown: true,
            show_granularity: true,
        };
        return (
            <CardContainer custom_header_props={custom_header_props} load_error={loaders.message_time_series_load_error} loading={loaders.message_time_series_loading} emtpy_condition={message_time_series.length === 0} show_custom_header>
                <Graph type="line" data={message_time_series} x_axis="timestamp" y_axis="count" color_key="type" scale={message_scale} height={350} padding={[60, 25, 50, 60]} legend />
            </CardContainer>
        );
    };

    renderTrainingMessagesTable = () => {
        const { screen_width } = this.props;
        const { training_message_filters, training_messages, training_message_pagination, loaders } = this.props.chatlog_details;
        const custom_header_props = {
            title: training_message_filters.isTrained ? "Trained Messages" : "Untrained Messages",
            search_placeholder: "Filter By Message",
            onSearch: this.handleTrainingMessageSearch,
            granularity_options: untrain_granularity,
            granularity_value: training_message_filters.isHandled,
            onChangeGranularity: this.handleTrainingMessageGranularityChange,
            date_range: [training_message_filters.startDate, training_message_filters.endDate],
            onChangeDateRange: this.handleTrainingMessagesDateRangeChange,
            switch_class: training_message_filters.isTrained ? "ori-bg-green" : "ori-bg-danger",
            switch_text: training_message_filters.isTrained ? "Trained" : "Untrained",
            switch_checked: training_message_filters.isTrained,
            onChangeSwitch: this.handleTrainingMessageSwitchChange,
            show_granularity: !training_message_filters.isTrained,
            show_search: true,
            show_date_range: true,
            show_switch: true,
            show_sync: loaders.training_messages_sync_required,
            sync_spin: loaders.training_messages_loading,
            onClickSync: this.fetchTrainingMessage,
        };
        return (
            <CardContainer custom_header_props={custom_header_props} container_height={450} emtpy_condition={training_messages.length === 0} load_error={loaders.training_messages_load_error} loading={loaders.training_messages_loading} show_custom_header>
                <div className="ori-t-pad-10">
                    <Table className="ori-table-default ori-table-header-bg-transparent" scroll={{ x: 600 }} size={screen_width < 768 ? "small" : "middle"} rowKey={uniqueId} pagination={false} columns={this.trainingMessageTableColumn} dataSource={training_messages} onChange={this.onChangeTrainingMessagePagination} />
                    <CustomPagination size="small" current={training_message_pagination.current} next_disabled={!training_message_pagination.more_pages} onChange={this.onChangeTrainingMessagePagination} />
                </div>
            </CardContainer>
        );
    };

    render() {
        const { screen_width, screen_orientation, intents, chatlog_details } = this.props;
        const { drawer_data } = this.state;
        const drawer_width = (screen_width < 480 || (screen_width >= 480 && screen_width < 768 && screen_orientation === ORIENTATION.LANDSCAPE)) ? screen_width : 450;

        return (
            <div className="ori-lr-pad-10 oriChatlogAnalyticsContainer">
                <CustomHeader container_class="ori-b-mrgn-10" title="Chatlog Analytics" bottom_bordered />
                {this.renderMessageTimeSeries()}
                <CustomHeader title="Teach Your Bot" bottom_bordered />
                {this.renderTrainingMessagesTable()}
                <Drawer className={classNames("ori-drawer ori-drawer-title-primary ori-drawer-title-single-line ori-drawer-body-with-header", { "ori-drawer-body-no-pad": drawer_data.show_message_training_form })} width={drawer_width} title={drawer_data.title} placement="right" maskClosable={false} visible={drawer_data.show_message_training_form} onClose={this.closeModal}>
                    {
                        drawer_data.show_message_training_form &&
                        <ErrorBoundary onErrorCallback={this.closeModal}>
                            <MessageTrainingForm intents={intents} intent_value={drawer_data.active_message.newIntent} onChangeIntent={this.onChangeIntent} selected_suggestions={drawer_data.active_message.utterances} suggestions={drawer_data.suggestions} onChangeCheckboxGroup={this.onChangeUtterances} handleCancelClick={this.closeModal} loading={chatlog_details.loaders.message_training} handleOkClick={this.trainMessage} />
                        </ErrorBoundary>
                    }
                </Drawer>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        screen_width: state.page_details.device_data.screen_width,
        screen_orientation: state.page_details.device_data.screen_orientation,
        allowedChannels: state.admin_details.allowedChannels,
        intents: state.nlp_details.intents,
        chatlog_details: state.chatlog_details,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        actions: bindActionCreators(Object.assign({}, { pageChanged, getAllIntents }, chatlogActions), dispatch),
    };
};

ChatlogAnalytics.propTypes = {
    allowedChannels: PropTypes.array,
    actions: PropTypes.object,
    screen_width: PropTypes.number,
    screen_orientation: PropTypes.string,
    chatlog_details: PropTypes.object,
    intents: PropTypes.array,
    history: PropTypes.object,
    match: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(ChatlogAnalytics);



// WEBPACK FOOTER //
// ./src/app/pagecontainer/modules/analytics/chatloganalytics/index.js